import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api, money, formatApiError, fileUrl, hexToHsl, mapEmbedUrl, mapLinkUrl, isValidTrPhone } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Clock, MapPin, Phone, ChevronLeft, Check, CalendarDays, Loader2, CheckCircle2, Store, User, Users, Settings2, Home,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { DICT, DF_LOCALE, DATE_LOCALE, LangFlags, getStoredLang, setStoredLang } from "@/lib/bookingI18n";

const fmtDate = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BookingPage() {
  const { slug } = useParams();
  const [lang, setLangState] = useState(getStoredLang);
  const setLang = (l) => { setLangState(l); setStoredLang(l); };
  const L = DICT[lang];
  const stepLabels = { service: L.stepService, staff: L.stepStaff, date: L.stepDate, time: L.stepTime, info: L.stepInfo };
  const loc = DATE_LOCALE[lang];
  const trDate = (d) => d.toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long" });

  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [staff, setStaff] = useState(null); // staff object OR "any"
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [info, setInfo] = useState({ customer_name: "", customer_phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const { data: biz, isLoading, isError, error } = useQuery({
    queryKey: ["public-business", slug],
    queryFn: () => api.get(`/public/business/${slug}`).then((r) => r.data),
    retry: false,
  });

  const hasStaff = (biz?.staff?.length ?? 0) > 0;
  const multiStaff = (biz?.staff?.length ?? 0) > 1;
  const stepKeys = hasStaff ? ["service", "staff", "date", "time", "info"] : ["service", "date", "time", "info"];
  const cur = stepKeys[step];
  const staffId = staff && staff !== "any" ? staff.id : undefined;

  useEffect(() => {
    if (biz && biz.staff?.length === 1 && staff === null) setStaff(biz.staff[0]);
  }, [biz]); // eslint-disable-line react-hooks/exhaustive-deps

  const dateStr = date ? fmtDate(date) : null;
  const { data: avail, isFetching: loadingSlots } = useQuery({
    queryKey: ["availability", slug, service?.id, dateStr, staffId ?? "any"],
    queryFn: () => api.get(`/public/business/${slug}/availability`, {
      params: { service_id: service.id, date: dateStr, ...(staffId ? { staff_id: staffId } : {}) },
    }).then((r) => r.data),
    enabled: !!service && !!dateStr && cur === "time",
  });

  const { data: assign } = useQuery({
    queryKey: ["assign", slug, service?.id, dateStr, time, staffId ?? "any"],
    queryFn: () => api.get(`/public/business/${slug}/assign`, { params: { service_id: service.id, date: dateStr, time } }).then((r) => r.data),
    enabled: staff === "any" && !!service && !!dateStr && !!time,
  });
  const assignedName = assign?.staff_name;
  const today = useMemo(() => {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  }, []);

  const disabledDays = useMemo(() => {
    if (!biz) return () => false;
    const dayKeys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    return (d) => {
      if (d < today) return true;
      const wh = biz.working_hours[dayKeys[d.getDay()]];
      return !wh || wh.closed;
    };
  }, [biz, today]);

  const [otp, setOtp] = useState({ open: false, code: "", busy: false, sending: false });

  const startOtp = async () => {
    setOtp((o) => ({ ...o, sending: true }));
    try {
      await api.post(`/public/business/${slug}/verify/start`, { phone: info.customer_phone });
      setOtp({ open: true, code: "", busy: false, sending: false });
      toast.success(L.otpSent);
    } catch (err) {
      toast.error(formatApiError(err));
      setOtp((o) => ({ ...o, sending: false }));
    }
  };

  const verifyOtp = async () => {
    setOtp((o) => ({ ...o, busy: true }));
    try {
      await api.post(`/public/business/${slug}/verify/check`, { phone: info.customer_phone, code: otp.code });
      setOtp({ open: false, code: "", busy: false, sending: false });
      await submit();
    } catch (err) {
      toast.error(formatApiError(err));
      setOtp((o) => ({ ...o, busy: false }));
    }
  };

  const submit = async () => {
    if (!isValidTrPhone(info.customer_phone)) {
      toast.error(L.phoneWarning);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/business/${slug}/reservations`, {
        service_id: service.id, date: dateStr, time, lang, ...(staffId ? { staff_id: staffId } : {}), ...info,
      });
      setDone(data);
    } catch (err) {
      if (err?.response?.status === 428) {
        setSubmitting(false);
        await startOtp();
        return;
      }
      toast.error(formatApiError(err));
      if (err?.response?.status === 409) setStep(stepKeys.indexOf("time"));
    } finally { setSubmitting(false); }
  };

  if (isLoading)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (isError || !biz) {
    const locked = error?.response?.status === 403;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold">{locked ? L.notAvailableTitle : L.notFoundTitle}</h1>
          <p className="text-muted-foreground mt-2">
            {locked ? L.notAvailableDesc : L.notFoundDesc}
          </p>
        </div>
      </div>
    );
  }

  if (done)
    return (
      <div className="min-h-screen grain-bg flex flex-col items-center justify-center p-5" style={hexToHsl(biz.theme_color) ? { "--primary": hexToHsl(biz.theme_color), "--ring": hexToHsl(biz.theme_color) } : {}}>
        <div className="w-full max-w-md flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2" data-testid="success-brand-link" aria-label="365Rez ana sayfa">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
              <CalendarDays className="w-5 h-5 text-primary-foreground" />
            </div>
            <div className="leading-tight text-left">
              <span className="font-display font-bold text-2xl block">365Rez</span>
              <span className="block text-[11px] text-muted-foreground" data-testid="success-brand-motto">{L.motto}</span>
            </div>
          </Link>
          <LangFlags lang={lang} onChange={setLang} />
        </div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center" data-testid="success-screen">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold">{L.successTitle}</h1>
          <p className="text-muted-foreground mt-2">{done.business_name} {L.successWaiting}</p>
          <div className="mt-6 rounded-xl bg-secondary p-5 text-left space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{L.nameRow}</span><span className="font-medium">{done.reservation.customer_name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{L.serviceLabel}</span><span className="font-medium">{done.reservation.service_name}</span></div>
            {done.reservation.staff_name && <div className="flex justify-between"><span className="text-muted-foreground">{L.staffLabel}</span><span className="font-medium">{done.reservation.staff_name}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">{L.dateLabel}</span><span className="font-medium">{new Date(done.reservation.date + "T00:00:00").toLocaleDateString(loc, { day: "numeric", month: "long", year: "numeric", weekday: "long" })}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{L.timeLabel}</span><span className="font-medium">{done.reservation.time}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{L.priceLabel}</span><span className="font-bold text-primary">{money(done.reservation.service_price)}</span></div>
          </div>
          {biz.address && (
            <div className="mt-6 text-left">
              <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" /> {L.locationLabel}</p>
              <div className="rounded-xl overflow-hidden border border-border">
                <iframe title="harita" src={mapEmbedUrl(biz.address)} className="w-full h-40 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <a href={mapLinkUrl(biz)} target="_blank" rel="noreferrer" data-testid="maps-link"
                className="flex items-center justify-center gap-1.5 mt-3 h-11 rounded-full border border-border text-sm font-semibold text-primary hover:bg-accent transition-colors">
                <MapPin className="w-4 h-4" /> {L.directions}
              </a>
            </div>
          )}
          {done.reservation.cancel_token && (
            <Link to={`/randevu/${done.reservation.cancel_token}`} data-testid="manage-booking-link"
              className="flex items-center justify-center gap-1.5 mt-4 h-11 rounded-full border border-border text-sm font-semibold hover:bg-accent transition-colors">
              <Settings2 className="w-4 h-4" /> {L.manageBooking}
            </Link>
          )}
          <p className="text-xs text-muted-foreground mt-4">{L.confirmSms}</p>
          <Button className="w-full rounded-full h-12 mt-4" onClick={() => window.location.reload()} data-testid="new-booking-btn">
            {L.newBooking}
          </Button>
          {biz.is_demo && (
            <Link to="/" data-testid="demo-success-home-link" className="block mt-3">
              <Button variant="outline" className="w-full rounded-full h-12">
                <Home className="w-4 h-4 mr-1.5" /> {L.demoCta}
              </Button>
            </Link>
          )}
        </motion.div>
        <Link to="/kayit" data-testid="powered-by-link"
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
          <CalendarDays className="w-3.5 h-3.5" /> {L.poweredBy}
        </Link>
      </div>
    );

  const canNext = (cur === "service" && service) || (cur === "staff" && staff) || (cur === "date" && date) || (cur === "time" && time);
  const canSubmit = info.customer_name.trim() && isValidTrPhone(info.customer_phone);
  const isLast = cur === "info";

  const themeHsl = hexToHsl(biz.theme_color);
  const themeStyle = themeHsl ? { "--primary": themeHsl, "--ring": themeHsl } : {};

  return (
    <div className="min-h-screen grain-bg" style={themeStyle} translate="no">
      {biz.is_demo && (
        <div className="sticky top-0 z-50 bg-primary text-primary-foreground" data-testid="demo-topbar">
          <div className="max-w-2xl mx-auto px-5 py-2.5 flex items-center justify-between gap-3">
            <span className="text-sm font-medium hidden sm:inline" dangerouslySetInnerHTML={{ __html: L.demoBanner.replace("demo", "<b>demo</b>") }} />
            <span className="text-sm font-medium sm:hidden">{L.demoBannerShort}</span>
            <div className="flex items-center gap-2 shrink-0">
              <LangFlags lang={lang} onChange={setLang} dark />
              <Link to="/"><Button size="sm" variant="ghost" className="h-8 gap-1 bg-white/15 hover:bg-white/25 text-primary-foreground" data-testid="demo-home-btn"><Home className="w-4 h-4" /> {L.home}</Button></Link>
              <Link to="/kayit"><Button size="sm" className="h-8 bg-white text-primary hover:bg-white/90 font-semibold" data-testid="demo-register-btn">{L.register}</Button></Link>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="booking-brand-link" aria-label="365Rez ana sayfa">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <CalendarDays className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="leading-tight">
            <span className="font-display font-bold text-lg block">365Rez</span>
            <span className="block text-[11px] text-muted-foreground" data-testid="booking-brand-motto">{L.motto}</span>
          </div>
        </Link>
        <LangFlags lang={lang} onChange={setLang} />
      </div>
      {otp.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="otp-modal">
          <div className="w-full max-w-sm rounded-2xl bg-card border border-border p-6">
            <h2 className="text-lg font-bold">{L.otpTitle}</h2>
            <p className="text-sm text-muted-foreground mt-1">{L.otpDesc} <span className="font-semibold text-foreground">{info.customer_phone}</span></p>
            <Input value={otp.code} onChange={(e) => setOtp((o) => ({ ...o, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
              inputMode="numeric" maxLength={6} placeholder="______" data-testid="otp-input"
              className="mt-4 text-center text-2xl tracking-[0.5em] h-14" />
            <Button className="w-full mt-4 h-12 rounded-full" disabled={otp.busy || otp.code.length !== 6} onClick={verifyOtp} data-testid="otp-verify-btn">
              {otp.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L.otpVerify}
            </Button>
            <div className="flex items-center justify-between mt-3">
              <button className="text-xs text-muted-foreground hover:text-primary" onClick={() => setOtp({ open: false, code: "", busy: false, sending: false })} data-testid="otp-cancel-btn">{L.otpCancel}</button>
              <button className="text-xs text-primary font-semibold disabled:opacity-50" disabled={otp.sending} onClick={startOtp} data-testid="otp-resend-btn">{L.otpResend}</button>
            </div>
          </div>
        </div>
      )}
      {biz.cover_url && (
        <div className="w-full h-40 sm:h-56 overflow-hidden relative" data-testid="cover-banner">
          <img src={fileUrl(biz.cover_url)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(0deg, hsl(${themeHsl || "243 75% 59%"} / 0.35), transparent)` }} />
        </div>
      )}
      <div className="bg-card border-b border-border">
        <div className="max-w-2xl mx-auto px-5 py-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl border border-border bg-secondary overflow-hidden flex items-center justify-center shrink-0">
            {biz.logo_url ? <img src={fileUrl(biz.logo_url)} alt="" className="w-full h-full object-cover" />
              : <Store className="w-6 h-6 text-muted-foreground" />}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold truncate" data-testid="business-name">{biz.name}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-muted-foreground mt-0.5">
              {biz.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {biz.phone}</span>}
              {biz.address && <span className="flex items-center gap-1 truncate"><MapPin className="w-3.5 h-3.5 shrink-0" /> {biz.address}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6">
          {stepKeys.map((key, i) => (
            <React.Fragment key={key}>
              <div className="flex items-center gap-2">
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors",
                  i <= step ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground")}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={cn("text-xs font-semibold hidden sm:block", i === step ? "text-foreground" : "text-muted-foreground")}>{stepLabels[key]}</span>
              </div>
              {i < stepKeys.length - 1 && <div className={cn("flex-1 h-0.5 rounded", i < step ? "bg-primary" : "bg-secondary")} />}
            </React.Fragment>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={cur + lang} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            {cur === "service" && (
              <div className="space-y-3" data-testid="step-service">
                <h2 className="text-lg font-semibold mb-1">{L.chooseService}</h2>
                {biz.services.length === 0 && <p className="text-muted-foreground">{L.noServices}</p>}
                {biz.services.map((s) => (
                  <button key={s.id} onClick={() => { setService(s); setTime(null); setStep(1); }}
                    className={cn("w-full text-left rounded-xl border p-4 transition-all duration-200 active:scale-[0.98]",
                      service?.id === s.id ? "border-primary bg-accent ring-2 ring-primary/20" : "border-border bg-card hover:border-primary/40")}
                    data-testid="service-option">
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <p className="font-semibold">{s.name}</p>
                        {s.description && <p className="text-sm text-muted-foreground mt-0.5">{s.description}</p>}
                        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration_minutes} {L.min}</p>
                      </div>
                      <span className="font-bold text-primary text-lg shrink-0">{money(s.price)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {cur === "staff" && (
              <div className="space-y-3" data-testid="step-staff">
                <h2 className="text-lg font-semibold mb-1">{L.chooseStaff}</h2>
                {multiStaff && (
                <button onClick={() => { setStaff("any"); setTime(null); setStep(2); }}
                  className={cn("w-full text-left rounded-xl border p-4 transition-all duration-200 active:scale-[0.98] flex items-center gap-3",
                    staff === "any" ? "border-primary bg-accent ring-2 ring-primary/20" : "border-border bg-card hover:border-primary/40")}
                  data-testid="staff-any-option">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-muted-foreground" /></div>
                  <div><p className="font-semibold">{L.anyStaff}</p><p className="text-sm text-muted-foreground">{L.anyStaffDesc}</p></div>
                </button>
                )}
                {biz.staff.map((p) => (
                  <button key={p.id} onClick={() => { setStaff(p); setTime(null); setStep(2); }}
                    className={cn("w-full text-left rounded-xl border p-4 transition-all duration-200 active:scale-[0.98] flex items-center gap-3",
                      staff?.id === p.id ? "border-primary bg-accent ring-2 ring-primary/20" : "border-border bg-card hover:border-primary/40")}
                    data-testid="staff-option">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><User className="w-5 h-5 text-primary" /></div>
                    <div><p className="font-semibold">{p.name}</p>{p.title && <p className="text-sm text-muted-foreground">{p.title}</p>}</div>
                  </button>
                ))}
              </div>
            )}

            {cur === "date" && (
              <div data-testid="step-date">
                <h2 className="text-lg font-semibold mb-3">{L.chooseDate}</h2>
                <div className="rounded-xl border border-border bg-card p-3 flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      setTime(null);
                      if (d) setStep(step + 1);
                    }}
                    disabled={disabledDays}
                    hidden={{ before: today }}
                    fromMonth={today}
                    showOutsideDays={false}
                    weekStartsOn={1}
                    locale={DF_LOCALE[lang]}
                  />
                </div>
                {date && <p className="text-center text-sm text-muted-foreground mt-3 capitalize">{trDate(date)}</p>}
              </div>
            )}

            {cur === "time" && (
              <div data-testid="step-time">
                <h2 className="text-lg font-semibold mb-3">{L.chooseTime}</h2>
                {loadingSlots ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
                ) : (avail?.slots?.length ?? 0) === 0 ? (
                  <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="no-slots">
                    <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    {L.noSlots}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                    {avail.slots.map((t) => (
                      <button key={t} onClick={() => { setTime(t); setStep(step + 1); }}
                        className={cn("h-12 rounded-xl border font-semibold text-sm transition-all duration-200 active:scale-[0.96]",
                          time === t ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/40")}
                        data-testid="time-slot">
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {cur === "info" && (
              <div className="space-y-4" data-testid="step-info">
                <h2 className="text-lg font-semibold">{L.yourInfo}</h2>
                <div className="rounded-xl bg-accent/60 border border-border p-4 text-sm space-y-1">
                  <p><span className="text-muted-foreground">{L.serviceLabel}:</span> <span className="font-medium">{service.name}</span></p>
                  {hasStaff && <p><span className="text-muted-foreground">{L.staffLabel}:</span> <span className="font-medium">{staff === "any" ? (assignedName || L.anyStaff) : staff?.name}</span></p>}
                  <p><span className="text-muted-foreground">{L.dateLabel}:</span> <span className="font-medium capitalize">{trDate(date)}</span></p>
                  <p><span className="text-muted-foreground">{L.timeLabel}:</span> <span className="font-medium">{time}</span></p>
                  <p><span className="text-muted-foreground">{L.priceLabel}:</span> <span className="font-bold text-primary">{money(service.price)}</span></p>
                </div>
                <div>
                  <Label>{L.nameLabel}</Label>
                  <Input value={info.customer_name} onChange={(e) => setInfo((i) => ({ ...i, customer_name: e.target.value }))}
                    className="h-14 mt-1.5 text-base" placeholder={L.namePlaceholder} data-testid="customer-name-input" />
                </div>
                <div>
                  <Label>{L.phoneLabel}</Label>
                  <Input value={info.customer_phone} onChange={(e) => setInfo((i) => ({ ...i, customer_phone: e.target.value }))}
                    className="h-14 mt-1.5 text-base" placeholder={L.phonePlaceholder} type="tel" data-testid="customer-phone-input" />
                  {info.customer_phone && !isValidTrPhone(info.customer_phone)
                    ? <p className="text-xs text-red-600 mt-1" data-testid="phone-warning">{L.phoneWarning}</p>
                    : <p className="text-xs text-muted-foreground mt-1">{L.phoneHint}</p>}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <Button variant="outline" className="rounded-full h-14 px-6" onClick={() => setStep((s) => s - 1)} data-testid="back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> {L.back}
            </Button>
          )}
          {!isLast ? (
            <Button className="rounded-full h-14 flex-1 text-base" disabled={!canNext} onClick={() => setStep((s) => s + 1)} data-testid="next-btn">
              {L.next}
            </Button>
          ) : (
            <Button className="rounded-full h-14 flex-1 text-base" disabled={!canSubmit || submitting} onClick={submit} data-testid="confirm-booking-btn">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : L.confirm}
            </Button>
          )}
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-5 pb-8 text-center">
        <Link to="/kayit" data-testid="powered-by-link"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
          <CalendarDays className="w-3.5 h-3.5" /> {L.poweredBy}
        </Link>
      </div>
    </div>
  );
}
